<script setup lang="ts">
import type { NuxtError } from '#app'

/**
 * Écran d'erreur du CMS.
 *
 * Nuxt monte ce composant à la place de `app.vue` dès qu'une erreur `fatal`
 * remonte — un `createError` de middleware ou de page, une route inconnue. Sans
 * ce fichier, c'est l'écran d'usine qui s'affiche : fond blanc, anglais, et un
 * « Go back home » qui ne tient aucun compte du contexte.
 *
 * Le layout est posé explicitement : `error.vue` remplace `app.vue`, et
 * `NuxtLayout` ne s'y applique pas de lui-même. On le garde pour que l'en-tête
 * et le menu restent là — une erreur sur un écran ne coupe pas l'accès au
 * reste du CMS.
 */
const props = defineProps<{ error: NuxtError }>()

const authStore = useAuthStore()

const statusCode = computed(() => Number(props.error?.statusCode) || 500)

/**
 * Libellés qu'écrit Nuxt lui-même, à distinguer de ceux du projet.
 *
 * Les deux arrivent par `statusMessage` : les `createError` du projet y mettent
 * un libellé français (« Accès réservé aux administrateurs », « Zone de menu
 * inconnue »), Nuxt un texte anglais générique (« Page not found: /xxx »). Seul
 * le premier a sa place à l'écran.
 */
const MESSAGES_USINE = [
  /^page not found/i,
  /^not found$/i,
  /^forbidden$/i,
  /^unauthorized$/i,
  /^internal server error$/i,
  /^bad request$/i,
]

/** Le message métier, quand il en existe un. */
const messageMetier = computed(() => {
  const brut = props.error?.statusMessage?.trim()
  if (!brut) return null
  // Un libellé d'usine n'apprend rien de plus que le titre déjà affiché :
  // « Page not found: /xxx » sous « Page introuvable » ne fait que réintroduire
  // l'anglais qu'on cherchait à sortir.
  if (MESSAGES_USINE.some(motif => motif.test(brut))) return null
  return brut
})

/**
 * Une session perdue se reconnaît au code 401, jamais au 403.
 *
 * Le 403 dit « cette session est valide mais n'a pas le droit » : proposer une
 * reconnexion y serait trompeur, se reconnecter au même compte ne changerait
 * rien. Le 401 dit « plus de session » : là, la relance a du sens.
 */
const sessionPerdue = computed(() => statusCode.value === 401)

interface Presentation {
  icone: string
  couleur: string
  titre: string
  explication: string
}

/**
 * Chaque famille d'erreur a sa formulation et son issue de secours : un accès
 * refusé et une adresse inconnue n'appellent ni le même ton, ni le même geste.
 */
const presentation = computed<Presentation>(() => {
  if (sessionPerdue.value) {
    return {
      icone: 'i-lucide-log-in',
      couleur: 'var(--ui-warning)',
      titre: 'Session expirée',
      explication: 'Votre session n\'est plus valide. Reconnectez-vous pour reprendre où vous en étiez.',
    }
  }

  if (statusCode.value === 403) {
    return {
      icone: 'i-lucide-shield-x',
      couleur: 'var(--ui-error)',
      titre: 'Accès refusé',
      explication: 'Votre compte n\'a pas les droits requis pour cet écran. Demandez-les à un administrateur si vous pensez en avoir besoin.',
    }
  }

  if (statusCode.value === 404) {
    return {
      icone: 'i-lucide-file-question',
      couleur: 'var(--ui-primary)',
      titre: 'Page introuvable',
      explication: 'Cette adresse ne correspond à aucun écran. Le lien est peut-être erroné, ou l\'élément a été supprimé.',
    }
  }

  return {
    icone: 'i-lucide-triangle-alert',
    couleur: 'var(--ui-error)',
    titre: 'Une erreur est survenue',
    explication: 'Le CMS n\'a pas pu afficher cet écran. Réessayez ; si l\'erreur persiste, signalez-la.',
  }
})

/**
 * Le retour arrière n'est proposé que s'il mène quelque part.
 *
 * `history.state.back` ne convient pas ici : une erreur fatale provoque un
 * rechargement complet de l'application, et l'état du routeur repart vierge —
 * il vaut `null` même quand on arrive d'un écran du CMS. `document.referrer`,
 * lui, survit au rechargement.
 *
 * Les deux conditions sont exigées ensemble : le référent dit d'où l'on vient,
 * `history.length` que la pile a bien une entrée où revenir. Sur une URL
 * ouverte directement — lien collé, favori, nouvel onglet — le référent est
 * vide, et le bouton ne s'affiche pas plutôt que de ne rien faire.
 */
const peutRevenir = ref(false)

onMounted(() => {
  const referent = document.referrer
  const memeOrigine = Boolean(referent) && referent.startsWith(`${window.location.origin}/`)
  // Un référent égal à l'adresse courante signalerait un rechargement sur
  // place : y « revenir » laisserait sur le même écran d'erreur.
  const autrePage = referent !== window.location.href
  peutRevenir.value = memeOrigine && autrePage && window.history.length > 1
})

/**
 * Sortir de l'écran d'erreur demande `clearError` : une navigation ordinaire
 * laisserait l'erreur en place et le CMS reviendrait ici.
 */
function versTableauDeBord() {
  clearError({ redirect: '/' })
}

function revenirEnArriere() {
  // `clearError` n'est pas appelé ici : l'écran d'erreur est né d'un
  // rechargement complet, et revenir en arrière en déclenche un autre — c'est
  // le navigateur qui remonte la page précédente, pas le routeur. `clearError`
  // n'aurait rien à défaire et sa redirection entrerait en concurrence avec
  // celle du navigateur.
  window.history.back()
}

function seReconnecter() {
  window.location.href = '/api/auth/login?state=%2F'
}

/**
 * Le détail technique ne s'affiche qu'en développement.
 *
 * En production il n'apprend rien à un éditeur et expose des chemins de
 * fichiers ; replié, il resterait une invitation à l'ouvrir pour n'y trouver
 * qu'une trace illisible.
 */
const detailTechnique = computed(() => {
  if (!import.meta.dev) return null
  const parts: string[] = []
  if (props.error?.message) parts.push(`Message : ${props.error.message}`)
  if (props.error?.stack) parts.push(`\n${props.error.stack}`)
  return parts.length ? parts.join('\n') : null
})

useHead({ title: `${presentation.value.titre} (${statusCode.value})` })
</script>

<template>
  <UApp>
    <!--
      La palette est montée ici aussi, et non seulement dans `app.vue` : Nuxt
      monte cet écran **à la place** du premier, et le raccourci y serait sinon
      muet — sur l'écran d'où l'on a justement le plus besoin d'aller ailleurs.
    -->
    <AdminCommandPalette v-if="authStore.authenticated" />

    <!--
      Le layout n'est conservé que pour une session ouverte : sans utilisateur,
      l'en-tête n'aurait ni avatar ni menu, et le menu latéral pointerait vers
      des écrans inaccessibles. L'écran nu est alors le bon cadre.
    -->
    <NuxtLayout :name="authStore.authenticated ? 'default' : false">
      <div class="flex items-center justify-center py-12">
        <div class="w-full max-w-xl text-center space-y-6">
          <div
            class="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
            :style="{ backgroundColor: `color-mix(in srgb, ${presentation.couleur} 12%, transparent)` }"
          >
            <UIcon :name="presentation.icone" class="text-3xl" :style="{ color: presentation.couleur }" />
          </div>

          <div class="space-y-2">
            <p class="text-sm font-medium tracking-wide text-[var(--ui-text-dimmed)]">
              Erreur {{ statusCode }}
            </p>
            <h1 class="text-2xl font-bold text-[var(--ui-text-highlighted)]">
              {{ presentation.titre }}
            </h1>
            <p class="text-sm text-[var(--ui-text-muted)]">
              {{ presentation.explication }}
            </p>
          </div>

          <!--
            Le message métier est repris tel quel : les `createError` du projet
            le rédigent déjà en français, et il dit précisément ce qui a manqué
            là où l'explication générique reste large.
          -->
          <div
            v-if="messageMetier"
            class="rounded-lg border border-[var(--ui-border)] bg-[var(--ui-bg-elevated)] px-4 py-3 text-sm text-[var(--ui-text)]"
          >
            {{ messageMetier }}
          </div>

          <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
            <UButton
              v-if="sessionPerdue"
              icon="i-lucide-log-in"
              class="justify-center"
              @click="seReconnecter"
            >
              Se reconnecter
            </UButton>
            <UButton
              v-else
              icon="i-lucide-layout-dashboard"
              class="justify-center"
              @click="versTableauDeBord"
            >
              Tableau de bord
            </UButton>

            <UButton
              v-if="peutRevenir"
              icon="i-lucide-arrow-left"
              color="neutral"
              variant="outline"
              class="justify-center"
              @click="revenirEnArriere"
            >
              Revenir en arrière
            </UButton>
          </div>

          <details v-if="detailTechnique" class="text-left">
            <summary class="cursor-pointer text-xs text-[var(--ui-text-dimmed)] hover:text-[var(--ui-text-muted)]">
              Détail technique (développement)
            </summary>
            <pre class="mt-2 max-h-64 overflow-auto rounded-lg bg-[var(--ui-bg-elevated)] p-3 text-xs text-[var(--ui-text-muted)] whitespace-pre-wrap break-all">{{ detailTechnique }}</pre>
          </details>
        </div>
      </div>
    </NuxtLayout>
  </UApp>
</template>
